import { deepClone, get } from '@direct-frontend/stdlib';
import { getFormStoreSingleton } from '../../store/store';
import { getFieldPathById } from '../../store/utils/getFieldPathByFieldId';
import { BaseField, BaseFields } from '../types';
import { walkField } from './walkFields';
import { $internals, FormActionsMap, FormInstance } from '../../form/types';
import { BaseValidationError } from '../../types';
import { setFieldValueAtPath } from './setFieldValueAtPath';
import { startFieldReactions, stopFieldReactions } from '../../form/reactions';

export interface ResetFieldOptions<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
> {
    form: FormInstance<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >;
    field: BaseField;
    fieldId: string;
    fieldValue: unknown;
}

/**
 * Функция ресета состояния филда и установки нового значения.
 * Используется в случае когда в поле устанавливается новое значение
 * и реакции для нового значения отличаются от реакций для старого
 * (например, изменилась длина массива или значение union поля - см. shouldResetField)
 *
 * - Останавливает все реакции поля (включая реакции дочерних полей)
 * - Устанавливает новое значение поля и пересоздает fieldState
 * - Запускает реакции всех новых полей соответствующих новому значению
 */
export function resetField<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    fieldValue,
    field,
    fieldId,
}: ResetFieldOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>) {
    const { setFieldValueAndResetState } = getFormStoreSingleton(form.name);

    stopFieldReactions({
        form,
        field,
        fieldId,
    });

    setFieldValueAndResetState({
        formInitializationId: form[$internals].formInitializationId,
        formFields: form[$internals].fields,
        fieldValue,
        fieldId,
    });

    startFieldReactions({
        form,
        field,
        fieldId,
    });
}

/**
 * Функция для определения, изменится ли структура полей при установки нового значения так,
 * что текущие запущенные реакции не будут подходить под новое значение поля
 *
 * Например:
 * - В новом значение длина массива отличается от текущего - нужно запустить/остановить реакции для добавленного/удаленного
 * - В новом значение ключ union поля отличается - нужно остановить реакции для старого объектного поля и запустить для нового
 */
export function shouldResetField<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>({
    form,
    fieldValue,
    field,
    fieldId,
}: ResetFieldOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue
>) {
    const { getFieldsState, getFormValue } = getFormStoreSingleton(form.name);

    const fieldsState =
        getFieldsState({
            formInitializationId: form[$internals].formInitializationId,
        }) ?? {};

    const prevFormValue = getFormValue({ formInitializationId: form[$internals].formInitializationId }) ?? {};
    const nextFormValue = setFieldValueAtPath(
        deepClone(prevFormValue),
        getFieldPathById(() => fieldsState, fieldId),
        fieldValue
    );

    let shouldResetField = false;

    walkField({
        field,
        fieldsState,
        formValue: nextFormValue,
        fieldId,
        callback: (childField, childFieldId) => {
            if (shouldResetField) return;

            // Поля-юнионы
            if (childField.type === 'union') {
                const path = getFieldPathById(() => fieldsState, childFieldId);

                const prevUnionValue = get<Record<string, unknown>>(prevFormValue, path);
                const nextUnionValue = get<Record<string, unknown>>(nextFormValue, path);

                const prevUnionKey = String(prevUnionValue?.[childField.key]);
                const nextUnionKey = String(nextUnionValue?.[childField.key]);

                // Если изменился ключ поля-юниона
                if (nextUnionKey !== prevUnionKey) {
                    shouldResetField = true;
                }
            }

            // Поля-массивы
            if (childField.type === 'array') {
                const path = getFieldPathById(() => fieldsState, childFieldId);

                const prevArrayValue = get<unknown[]>(prevFormValue, path);
                const nextArrayValue = get<unknown[]>(nextFormValue, path);

                // Если изменилось количество элементов массива
                if (nextArrayValue?.length !== prevArrayValue?.length) {
                    shouldResetField = true;
                }
            }
        },
    });

    return shouldResetField;
}
